/**
 * AdminRevenueBreakdown.tsx
 * 
 * Reusable component for displaying revenue breakdown in admin dashboard.
 * Maintains exact visual design from AdminFinancials page.
 */

import React from 'react'
import { PieChart } from 'lucide-react'

interface RevenueBreakdownProps {
  revenueBreakdown: {
    subscriptions: number
    credits: number
    oneTime: number
  }
  totalRevenue: number
}

const AdminRevenueBreakdown: React.FC<RevenueBreakdownProps> = ({
  revenueBreakdown,
  totalRevenue
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Revenue Breakdown</h3>
        <PieChart className="h-5 w-5 text-gray-400" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm font-medium text-gray-700">Subscriptions</span>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(revenueBreakdown.subscriptions)}
            </p>
            <p className="text-xs text-gray-500">
              {((revenueBreakdown.subscriptions / totalRevenue) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm font-medium text-gray-700">Credits</span>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(revenueBreakdown.credits)}
            </p>
            <p className="text-xs text-gray-500">
              {((revenueBreakdown.credits / totalRevenue) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-sm font-medium text-gray-700">One-time</span>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(revenueBreakdown.oneTime)}
            </p>
            <p className="text-xs text-gray-500">
              {((revenueBreakdown.oneTime / totalRevenue) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminRevenueBreakdown 